import { useCallback, useMemo, useState } from 'react'
import { mdiPlus } from '@mdi/js'
import { useIntl } from 'react-intl'

import { useAppSelector } from 'app/hooks'
import { TableLoader } from 'components/Table'
import {
  HomeConfigPlusOpenModalIcon,
  HomeConfigPlusOpenModalIconButton,
  ConfigFilesStyledButton,
} from '../styles'
import {
  homeJobSelector,
  matchingHomeConfigsSelector,
  notMatchingHomeConfigsSelector,
} from 'features/home/selectors'
import { HomeConfigFilesTable } from '../..'
import { homeConfigFilesMessages } from '../messages'
import { WhiteBlock } from 'components/Layout'
import theme from 'theme_'
import HomeConfigUploadModal from './HomeConfigUploadModal'
import {
  HomeConfigFilesContainer,
  HomeConfigFilesHeaderContainer,
  HomeConfigFilesHeaderTitle,
  HomeConfigRadioTilesContainer,
} from './styles'
import { RadioTiles } from 'components/RadioTiles'
import { isChrome, isEdgeChromium } from 'react-device-detect'

enum TABLE_SELECTOR {
  matching = 'matching',
  notMatching = 'notMatching',
}
interface Option {
  label: string
  value: TABLE_SELECTOR
  defaultChecked: boolean
}
interface HomeConfigFilesProps {
  selector: keyof typeof TABLE_SELECTOR
  title: string
}

function HomeConfigFiles({ selector, title }: HomeConfigFilesProps) {
  const { formatMessage } = useIntl()
  const [isOpen, setIsOpen] = useState(false)
  const [isMatching, setIsMatching] = useState(TABLE_SELECTOR.matching)
  const homeJobLoaded = useAppSelector(homeJobSelector.isFinished)
  const matchingHomeConfigs = useAppSelector(matchingHomeConfigsSelector)
  const notMatchingHomeConfigs = useAppSelector(notMatchingHomeConfigsSelector)

  const isRadioTilesVisible = selector === TABLE_SELECTOR.notMatching

  const jobsListsOptions: Option[] = [
    {
      label: formatMessage(homeConfigFilesMessages.matching),
      value: TABLE_SELECTOR.matching,
      defaultChecked: isMatching === TABLE_SELECTOR.matching,
    },
    {
      label: formatMessage(homeConfigFilesMessages.notMatching),
      value: TABLE_SELECTOR.notMatching,
      defaultChecked: isMatching === TABLE_SELECTOR.notMatching,
    },
  ]

  const handleConfigTableToggle = useCallback((val: TABLE_SELECTOR) => {
    setIsMatching(val)
  }, [])

  const chosenSelector = useMemo(
    () =>
      isMatching === TABLE_SELECTOR.matching
        ? matchingHomeConfigs
        : notMatchingHomeConfigs,
    [isMatching, matchingHomeConfigs, notMatchingHomeConfigs]
  )

  const handleCloseConfigUpload = () => setIsOpen(false)
  const handleOpenConfigUpload = () => setIsOpen(true)

  return (
    <HomeConfigFilesContainer>
      <HomeConfigFilesHeaderContainer>
        <HomeConfigFilesHeaderTitle>{title}</HomeConfigFilesHeaderTitle>
        {isRadioTilesVisible && (
          <HomeConfigRadioTilesContainer>
            <RadioTiles
              options={jobsListsOptions}
              name="config_table"
              onChange={handleConfigTableToggle}
            />
          </HomeConfigRadioTilesContainer>
        )}
        <HomeConfigPlusOpenModalIconButton onClick={handleOpenConfigUpload}>
          <HomeConfigPlusOpenModalIcon
            path={mdiPlus}
            color={theme.colors.horizonOrange}
            size={1}
          />
        </HomeConfigPlusOpenModalIconButton>
        <ConfigFilesStyledButton
          variant="secondary"
          onClick={handleOpenConfigUpload}
          disabled={!isChrome&&!isEdgeChromium}
          title="Upload supported in Chrome/Edge only"
        >
          {formatMessage(homeConfigFilesMessages.uploadFile)}
          {(!isChrome && !isEdgeChromium) ? " (Chrome/Edge Only) " : " "}
        </ConfigFilesStyledButton>
      </HomeConfigFilesHeaderContainer>

      {homeJobLoaded ? (
        <>
          {chosenSelector?.length ? (
            <HomeConfigFilesTable
              data={chosenSelector}
              isDeployShown={!isRadioTilesVisible}
            />
          ) : (
            <WhiteBlock bigPadding>
              {formatMessage(homeConfigFilesMessages.noUploaded)}
            </WhiteBlock>
          )}
        </>
      ) : (
        <TableLoader />
      )}
      <HomeConfigUploadModal
        isOpen={isOpen}
        handleClose={handleCloseConfigUpload}
      />
    </HomeConfigFilesContainer>
  )
}

export default HomeConfigFiles
